import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wallet, Calendar, ScrollText, ShoppingBag, Sparkles, User, TrendingUp, Star, ArrowUpRight, Plus, Minus } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [kundalis, setKundalis] = useState([]);
  const [wallet, setWallet] = useState({ balance: 0, transactions: [] });
  const [selectedKundali, setSelectedKundali] = useState(null);

  useEffect(() => {
    if (user === false) { nav("/login"); return; }
    if (!user) return;
    (async () => {
      try {
        const [b, o, k, w] = await Promise.all([
          api.get("/bookings"),
          api.get("/orders"),
          api.get("/kundali/list"),
          api.get("/wallet"),
        ]);
        setBookings(b.data);
        setOrders(o.data);
        setKundalis(k.data);
        setWallet(w.data);
      } catch (e) { console.error(e); }
    })();
  }, [user, nav]);

  if (!user || user === false) {
    return <div className="max-w-7xl mx-auto px-6 py-20 text-center text-[#A0A4AB]">Redirecting…</div>;
  }

  const isProvider = user.role === "astrologer" || user.role === "pandit";

  const recharge = async (amount) => {
    try {
      const { data } = await api.post("/wallet/txn", { amount, kind: "credit", note: "Wallet recharge" });
      setWallet(await (await api.get("/wallet")).data);
      await refreshMe();
      toast.success(`₹${amount} added to wallet`);
    } catch (e) { toast.error("Recharge failed"); }
  };

  const spend = async (amount) => {
    try {
      await api.post("/wallet/txn", { amount, kind: "debit", note: "Consultation charge" });
      setWallet((await api.get("/wallet")).data);
      await refreshMe();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const TABS = [
    { key: "overview", label: "Overview", icon: TrendingUp },
    { key: "bookings", label: isProvider ? "Appointments" : "Bookings", icon: Calendar },
    { key: "kundali", label: "Kundali", icon: Sparkles },
    { key: "orders", label: "Orders", icon: ShoppingBag },
    { key: "wallet", label: "Wallet", icon: Wallet },
    { key: "referral", label: "Referral", icon: User },
    { key: "learning", label: "My Courses", icon: Sparkles },
  ];
  if (user.role === "admin") TABS.push({ key: "admin", label: "Admin", icon: User });

  const StatCard = ({ label, value, sub }) => (
    <div className="p-6 rounded-xl bg-[#0A0C10] border border-white/10">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]/80">{label}</div>
      <div className="font-serif text-3xl mt-2">{value}</div>
      {sub && <div className="text-xs text-[#A0A4AB] mt-1">{sub}</div>}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 flex-wrap mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">
            {user.role} Dashboard
          </div>
          <h1 className="font-serif text-4xl">Namaste, <span className="gold-gradient">{user.name?.split(" ")[0]}</span></h1>
          <div className="text-sm text-[#A0A4AB] mt-1">Your journey continues here.</div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/change-password" data-testid="change-password-link" className="px-4 py-2 rounded-full border border-white/15 text-sm text-[#A0A4AB] hover:border-[#D4AF37] hover:text-[#D4AF37] transition">
            Change Password
          </Link>
          <Link to="/kundali" className="px-4 py-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#E5C865] transition inline-flex items-center gap-2">
            <Sparkles size={14}/> New Kundali
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 mb-8 overflow-x-auto">
        {TABS.map((t) => {
          const Ic = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`dashboard-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-5 py-3 text-sm inline-flex items-center gap-2 border-b-2 transition ${
                tab === t.key ? "border-[#D4AF37] text-[#D4AF37]"
                              : "border-transparent text-[#A0A4AB] hover:text-[#EAE6DF]"
              }`}
            >
              <Ic size={14}/> {t.label}
            </button>
          );
        })}
      </div>

      {/* OVERVIEW */}
      {tab === "overview" && (
        <div className="space-y-6">
          <div className="grid md:grid-cols-4 gap-4">
            <StatCard label="Wallet" value={`₹${wallet.balance.toFixed(0)}`} sub="Available balance" />
            <StatCard label={isProvider ? "Sessions" : "Bookings"} value={bookings.length} sub="All time" />
            <StatCard label="Kundalis" value={kundalis.length} sub="Generated" />
            <StatCard label="Orders" value={orders.length} sub="Marketplace" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-6 rounded-xl bg-[#0A0C10] border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <div className="font-serif text-xl">Recent Bookings</div>
                <button onClick={() => setTab("bookings")} className="text-xs text-[#D4AF37]">View all</button>
              </div>
              {bookings.length === 0 && <div className="text-sm text-[#A0A4AB]">No bookings yet.</div>}
              <div className="space-y-3">
                {bookings.slice(0, 4).map((b) => (
                  <div key={b.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <div>
                      <div className="text-sm capitalize">{b.service_type.replace("_"," ")}</div>
                      <div className="text-xs text-[#A0A4AB]">{new Date(b.scheduled_at).toLocaleString("en-IN")}</div>
                    </div>
                    <div className="text-sm text-[#D4AF37]">₹{b.amount}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 rounded-xl bg-[#0A0C10] border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <div className="font-serif text-xl">Quick Actions</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Link to="/kundali" className="p-4 rounded-lg border border-white/10 hover:border-[#D4AF37] transition text-center">
                  <Sparkles className="mx-auto text-[#D4AF37]" size={20}/><div className="text-xs mt-2">New Kundali</div>
                </Link>
                <Link to="/astrologers" className="p-4 rounded-lg border border-white/10 hover:border-[#D4AF37] transition text-center">
                  <Star className="mx-auto text-[#D4AF37]" size={20}/><div className="text-xs mt-2">Book Astrologer</div>
                </Link>
                <Link to="/shop" className="p-4 rounded-lg border border-white/10 hover:border-[#D4AF37] transition text-center">
                  <ShoppingBag className="mx-auto text-[#D4AF37]" size={20}/><div className="text-xs mt-2">Shop</div>
                </Link>
                <button onClick={() => setTab("wallet")} className="p-4 rounded-lg border border-white/10 hover:border-[#D4AF37] transition text-center">
                  <Wallet className="mx-auto text-[#D4AF37]" size={20}/><div className="text-xs mt-2">Recharge</div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* BOOKINGS */}
      {tab === "bookings" && (
        <div className="p-6 rounded-xl bg-[#0A0C10] border border-white/10">
          {bookings.length === 0 ? (
            <div className="py-12 text-center">
              <div className="font-deva text-4xl text-[#D4AF37] mb-3">ॐ</div>
              <div className="text-[#A0A4AB]">No bookings yet.</div>
              <Link to="/astrologers" className="mt-4 inline-block px-5 py-2 rounded-full bg-[#D4AF37] text-black text-sm">Book Now</Link>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-white/10">
                  <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Service</th>
                  <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Scheduled</th>
                  <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Amount</th>
                  <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Status</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-b border-white/5 last:border-0">
                    <td className="py-3 capitalize">{b.service_type.replace("_"," ")}</td>
                    <td className="py-3 text-[#A0A4AB]">{new Date(b.scheduled_at).toLocaleString("en-IN")}</td>
                    <td className="py-3 text-[#D4AF37]">₹{b.amount}</td>
                    <td className="py-3"><span className="px-2 py-1 rounded-full text-xs bg-[#12151C] border border-white/10 capitalize">{b.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* KUNDALI */}
      {tab === "kundali" && (
        <div className="grid md:grid-cols-12 gap-4">
          <div className="md:col-span-4 p-6 rounded-xl bg-[#0A0C10] border border-white/10">
            <div className="font-serif text-xl mb-4">Your Kundalis</div>
            {kundalis.length === 0 && <div className="text-sm text-[#A0A4AB]">None yet. <Link to="/kundali" className="text-[#D4AF37]">Generate →</Link></div>}
            <div className="space-y-2">
              {kundalis.map((k) => (
                <button
                  key={k.id}
                  data-testid={`kundali-item-${k.id}`}
                  onClick={() => setSelectedKundali(k)}
                  className={`w-full text-left p-3 rounded-lg border transition ${
                    selectedKundali?.id === k.id ? "border-[#D4AF37] bg-[#D4AF37]/5"
                                                 : "border-white/10 hover:border-white/20"
                  }`}
                >
                  <div className="text-sm text-[#EAE6DF]">{k.input.full_name}</div>
                  <div className="text-xs text-[#A0A4AB]">{k.input.dob} · {k.input.pob}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="md:col-span-8 p-6 rounded-xl bg-[#0A0C10] border border-white/10 min-h-[400px]">
            {selectedKundali ? <Markdown text={selectedKundali.interpretation} /> :
             <div className="text-[#A0A4AB]">Select a Kundali to view.</div>}
          </div>
        </div>
      )}

      {/* ORDERS */}
      {tab === "orders" && (
        <div className="p-6 rounded-xl bg-[#0A0C10] border border-white/10">
          {orders.length === 0 ? (
            <div className="py-12 text-center text-[#A0A4AB]">No orders yet.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {orders.map((o) => (
                <div key={o.id} className="p-4 rounded-lg border border-white/10 flex gap-4">
                  {o.product_image && <img src={o.product_image} className="w-20 h-20 object-cover rounded" />}
                  <div className="flex-1">
                    <div className="text-sm">{o.product_name}</div>
                    <div className="text-xs text-[#A0A4AB] mt-1">Qty {o.quantity} · ₹{o.total}</div>
                    <div className="text-xs text-[#D4AF37] mt-1 capitalize">{o.status}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* WALLET */}
      {tab === "wallet" && (
        <div className="grid md:grid-cols-12 gap-4">
          <div className="md:col-span-5 p-8 rounded-xl bg-gradient-to-br from-[#12151C] to-[#0A0C10] border border-[#D4AF37]/30">
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Wallet Balance</div>
            <div className="font-serif text-5xl mt-3 gold-gradient">₹{wallet.balance.toFixed(2)}</div>
            <div className="grid grid-cols-3 gap-2 mt-6">
              {[100, 500, 1000, 2000].map((a) => (
                <button
                  key={a}
                  data-testid={`recharge-${a}`}
                  onClick={() => recharge(a)}
                  className="py-2 rounded-full border border-white/15 text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition inline-flex items-center justify-center gap-1"
                >
                  <Plus size={12}/> {a}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-[#6E737D] mt-3">Demo mode — instant credit. Live payments via Stripe/Razorpay coming soon.</div>
          </div>

          <div className="md:col-span-7 p-6 rounded-xl bg-[#0A0C10] border border-white/10">
            <div className="font-serif text-xl mb-4">Recent Transactions</div>
            {wallet.transactions.length === 0 ? (
              <div className="text-sm text-[#A0A4AB]">No transactions yet.</div>
            ) : (
              <div className="space-y-2">
                {wallet.transactions.map((t) => (
                  <div key={t.id} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${t.kind === "credit" ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>
                        {t.kind === "credit" ? <Plus size={14}/> : <Minus size={14}/>}
                      </div>
                      <div>
                        <div className="text-sm capitalize">{t.note || t.kind}</div>
                        <div className="text-xs text-[#A0A4AB]">{new Date(t.created_at).toLocaleString("en-IN")}</div>
                      </div>
                    </div>
                    <div className={`text-sm ${t.kind === "credit" ? "text-emerald-400" : "text-red-400"}`}>
                      {t.kind === "credit" ? "+" : "-"}₹{t.amount}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "admin" && (
        <div className="text-center py-12">
          <Link to="/admin" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D4AF37] text-black">
            Open Admin Panel <ArrowUpRight size={16}/>
          </Link>
        </div>
      )}

      {tab === "referral" && <ReferralTab />}
      {tab === "learning" && <MyLearningTab />}
    </div>
  );
}

function ReferralTab() {
  const [data, setData] = React.useState(null);
  React.useEffect(() => { api.get("/referral/my").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-[#A0A4AB]">Loading…</div>;
  const link = `${window.location.origin}/register?ref=${data.code}`;
  const copy = () => {
    navigator.clipboard.writeText(link);
    toast.success("Referral link copied");
  };
  const share = () => {
    const text = `Join me on Vedacosmos — get ₹100 free wallet credit with my code: ${data.code}\n${link}`;
    if (navigator.share) navigator.share({ title: "Vedacosmos", text }); else copy();
  };
  return (
    <div className="grid md:grid-cols-12 gap-4">
      <div className="md:col-span-5 p-8 rounded-xl bg-gradient-to-br from-[#12151C] to-[#0A0C10] border border-[#D4AF37]/30">
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Your Referral Code</div>
        <div className="font-serif text-4xl gold-gradient mt-3 tracking-widest" data-testid="referral-code">{data.code}</div>
        <div className="text-xs text-[#A0A4AB] mt-2">Share this code — friend gets ₹100, you get ₹200 on their signup.</div>
        <div className="mt-6 flex gap-2">
          <input readOnly value={link} className="flex-1 px-3 py-2 rounded bg-[#050507] border border-white/10 text-xs text-[#A0A4AB]"/>
          <button data-testid="copy-referral-btn" onClick={copy} className="px-4 py-2 rounded bg-[#D4AF37] text-black text-xs font-medium">Copy</button>
        </div>
        <button data-testid="share-referral-btn" onClick={share} className="w-full mt-3 py-2 rounded-full border border-white/15 text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition">
          Share
        </button>
      </div>
      <div className="md:col-span-7 p-6 rounded-xl bg-[#0A0C10] border border-white/10">
        <div className="flex items-center justify-between mb-4">
          <div className="font-serif text-xl">Your Invites</div>
          <div className="text-right">
            <div className="font-serif text-2xl text-emerald-400">₹{data.total_earned}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#A0A4AB]">Total earned</div>
          </div>
        </div>
        {data.invited.length === 0 ? (
          <div className="py-8 text-center text-[#A0A4AB] text-sm">No invites yet. Share your code and earn ₹200 per signup!</div>
        ) : (
          <div className="space-y-2">
            {data.invited.map((r) => (
              <div key={r.id} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                <div>
                  <div className="text-sm">{r.referred_name}</div>
                  <div className="text-xs text-[#A0A4AB]">{new Date(r.created_at).toLocaleDateString("en-IN")}</div>
                </div>
                <div className="text-sm text-emerald-400">+₹{r.bonus_paid}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MyLearningTab() {
  const [items, setItems] = React.useState([]);
  React.useEffect(() => { api.get("/enrollments/my").then((r) => setItems(r.data)); }, []);
  return (
    <div>
      {items.length === 0 ? (
        <div className="py-12 text-center">
          <div className="text-[#A0A4AB] mb-4">No courses enrolled yet.</div>
          <Link to="/learn" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#D4AF37] text-black">
            Explore Courses <ArrowUpRight size={14}/>
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((e) => (
            <Link key={e.id} to={`/learn/${e.course_id}`} className="p-6 rounded-xl bg-[#0A0C10] border border-white/10 hover:border-[#D4AF37]/40 transition block">
              <div className="text-xs text-[#D4AF37]">{e.progress}% complete</div>
              <div className="font-serif text-lg mt-1">{e.course_name}</div>
              <div className="mt-3 h-2 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full bg-[#D4AF37]" style={{ width: `${e.progress}%` }}/>
              </div>
              {e.certificate_issued && (
                <div className="mt-3 text-xs text-emerald-400 inline-flex items-center gap-1">
                  ✓ Certificate {e.certificate_id}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
